import React, { useMemo } from 'react';
import { Input, Segmented, Select, Typography } from 'antd';

import ConditionBuilder from '../common/ConditionBuilder';
import DateField from '../common/DateField';
import FilterBuilder from '../common/FilterBuilder';
import FilterChips from '../common/FilterChips';
import TermText from './TermText';
import { optionSearchFilter } from '../Input';
import { ME_VALUE } from '../../utils/userElements';
import { columnsOfGroup } from '../../utils/entryRules';
import {
	EVENT_VERBS,
	MAX_DATE_ELEMENTS,
	MAX_OFFSETS,
	SHAPES,
	SHAPE_OPTIONS,
	defaultCoalesce,
	describeTriggerDraft,
} from './notificationEditorModel';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

const { Text } = Typography;

const DIRECTIONS = [
	{ value: 'before', label: 'before' },
	{ value: 'after', label: 'after' },
];

const COALESCE = [
	{ value: 'item', label: 'One notice per record' },
	{ value: 'date', label: 'One notice per date' },
];
// A reminder about each entry's date (KPR-836): by default every entry's date
// is its own notice; folded, one notice lists them all.
const ENTRY_COALESCE = [
	{ value: 'date', label: 'One notice per entry date' },
	{ value: 'item', label: 'One notice per item' },
];

const DATE_TYPES = ['date', 'date-time'];

const labelOf = element => element?.label?.singular || element?.name || '';

/**
 * A watched date's option value. The item's own dates keep their names; a
 * repeating group's date column (KPR-836) is spelled as a JSON pair, which no
 * element name can be (a name starts with a letter or a digit), so one picker
 * can offer both and tell them apart.
 */
const columnValue = (group, column) => JSON.stringify([group, column]);
export function decodeDateValue(value) {
	const text = `${value ?? ''}`;
	if (text.startsWith('[')) {
		try {
			const [group, name] = JSON.parse(text);
			if (typeof group === 'string' && typeof name === 'string') return { group, name };
		} catch (e) { /* not a pair: read as a name */ }
	}
	return { group: '', name: text };
}

/** The read-back line: muted prose with the NAMES inside it in ink. */
export function ReadBack({ text, className = 'notification-editor__readback' }) {
	if (!text) return null;
	return (
		<p className={className}>
			<TermText text={text} termClassName="notification-editor__term" />
		</p>
	);
}

/**
 * Panel 2 — **When it fires**: a Segmented that picks the shape, a row of
 * inline slots that reads as a sentence, and the generated line beneath it
 * saying what the slots add up to in plain words.
 *
 * The slots ARE the controls. A person builds "Card type *issue* is created"
 * by reading left to right and answering each box, rather than by filling a
 * form of labelled fields and then imagining what it means — which is the
 * whole difference between an editor a manager can use and one only its
 * author can. The read-back is generated (`describeTriggerDraft`), never
 * typed here, so a sentence cannot go stale behind the controls that made it.
 *
 * Three shapes over two stored triggers: *happens* and *changes* are both
 * `item-event` (the second adds `elementsChanged` and an edge-triggered
 * equality — "becomes" IS the edge), and *approaches* is `threshold`, whose
 * rungs never touch KQL at all (Decision 5). The optional condition beside
 * the sentence is the shared `FilterBuilder` over the card's own vocabulary,
 * summarised by the shared `FilterChips` — the same two components the items
 * list, the grant filters and the group rules use.
 */
export default function TriggerSentence({
	draft,
	onChange,
	cardOptions = [],
	elements = [],
	fields = [],
	collectionId,
	allowMe = false,
	userOptions = [],
}) {
	const patch = next => onChange({ ...draft, ...next });

	const elementOptions = useMemo(
		() => (elements || []).filter(el => el?.name).map(el => ({ value: el.name, label: labelOf(el), searchText: labelOf(el) })),
		[elements]
	);
	const itemDateOptions = useMemo(
		() => (elements || [])
			.filter(el => el?.name && DATE_TYPES.includes(el.dataType))
			.map(el => ({ value: el.name, label: labelOf(el), searchText: labelOf(el) })),
		[elements]
	);
	// The card's repeating groups with a date or date-time column (KPR-836):
	// each entry's date can be watched, one ladder per entry. The last of a
	// name wins, as a merged element list reads.
	const dateGroups = useMemo(() => {
		const byName = new Map();
		(elements || []).forEach(el => { if (el?.name) byName.set(el.name, el); });
		return [...byName.values()]
			.filter(el => el.dataType === 'repeating-group')
			.map(group => ({ group, columns: columnsOfGroup(group).filter(col => DATE_TYPES.includes(col.dataType)) }))
			.filter(entry => entry.columns.length);
	}, [elements]);
	// One picker, both kinds: the item's own dates, then each group's date
	// columns under a heading of their own (antd's option groups). With no
	// group to offer it is the flat list it always was.
	const dateOptions = useMemo(() => {
		if (!dateGroups.length) return itemDateOptions;
		return [
			...(itemDateOptions.length ? [{ label: 'This item', options: itemDateOptions }] : []),
			...dateGroups.map(({ group, columns }) => ({
				label: `Each ${labelOf(group)} entry`,
				options: columns.map(col => ({
					value: columnValue(group.name, col.name),
					label: `${labelOf(group)} · ${labelOf(col)}`,
					searchText: `${labelOf(group)} ${labelOf(col)}`,
				})),
			})),
		];
	}, [dateGroups, itemDateOptions]);
	const entryGroup = draft.entryGroup || '';
	const watchedGroup = useMemo(
		() => (entryGroup ? ((elements || []).filter(el => el?.name === entryGroup && el.dataType === 'repeating-group').pop() || null) : null),
		[entryGroup, elements]
	);
	const watchedColumns = useMemo(() => columnsOfGroup(watchedGroup), [watchedGroup]);
	const dateValues = entryGroup
		? (draft.dateElements || []).map(name => columnValue(entryGroup, name))
		: (draft.dateElements || []);
	// The newest pick decides which kind of date this reminder watches: the
	// item's own, or ONE group's columns — one notice cannot be about both.
	const pickDates = (values) => {
		const before = new Set(dateValues);
		const added = values.filter(value => !before.has(value));
		const lead = added.length ? decodeDateValue(added[added.length - 1]) : { group: entryGroup };
		const names = values.map(decodeDateValue)
			.filter(pick => pick.group === lead.group)
			.map(pick => pick.name)
			.slice(0, MAX_DATE_ELEMENTS);
		const next = { dateElements: names, entryGroup: lead.group };
		if (lead.group !== entryGroup) {
			next.coalesce = defaultCoalesce(lead.group);
			next.entryConditionKql = '';
			// A column of the old group's entry is no column of the new one
			// (KPR-874): the criterion keeps its kind and asks again.
			if (draft.resolve?.column) next.resolve = { ...draft.resolve, column: '' };
		}
		patch(next);
	};

	const changed = (elements || []).find(el => el?.name === draft.changedElement) || null;
	const elementLabel = name => labelOf((elements || []).find(el => el?.name === name)) || name;
	const columnLabel = name => labelOf(watchedColumns.find(col => col.name === name)) || name;

	// "Me" is legal at the personal tier and only there (Decision 8): a
	// collection rule runs with nobody to be, and the API refuses it — so the
	// option is left out rather than offered and then rejected.
	const becomesOptions = useMemo(() => {
		if (changed?.dataType === 'user') {
			return [...(allowMe ? [{ value: ME_VALUE, label: 'Me' }] : []), ...userOptions];
		}
		if (changed?.dataType === 'choice') {
			return (changed.options?.choices || []).map(choice => ({
				value: `${choice?.value ?? choice?.label ?? ''}`,
				label: `${choice?.label ?? choice?.value ?? ''}`,
			}));
		}
		if (changed?.dataType === 'boolean') {
			return [
				{ value: true, label: changed.options?.trueLabel || 'Yes' },
				{ value: false, label: changed.options?.falseLabel || 'No' },
			];
		}
		return null;
	}, [changed, allowMe, userOptions]);

	const becomesLabel = becomesOptions
		? (becomesOptions.find(option => option.value === draft.becomes)?.label || '')
		: (draft.becomes === undefined || draft.becomes === '' ? '' : `${draft.becomes}`);

	const cardPicker = (
		<Select
			className="notification-sentence__slot notification-sentence__slot--card"
			aria-label="Card this watches"
			placeholder="a card"
			value={draft.cardId || undefined}
			onChange={cardId => patch({
				cardId, changedElement: '', becomes: undefined, dateElements: [], entryGroup: '', entryConditionKql: '', conditionKql: '',
				...(draft.resolve?.column ? { resolve: { ...draft.resolve, column: '' } } : {}),
			})}
			options={cardOptions}
			showSearch
			filterOption={optionSearchFilter}
			popupClassName="card-picker-dropdown"
			popupMatchSelectWidth={false}
		/>
	);

	return (
		<section className="form-panel notification-editor__panel" data-testid="notification-editor-trigger">
			<div className="micro-label form-panel__kicker">When it fires</div>

			<Segmented
				className="notification-editor__shapes"
				aria-label="What sets it off"
				value={draft.shape}
				onChange={shape => patch({ shape })}
				options={SHAPE_OPTIONS}
			/>

			{draft.shape === SHAPES.HAPPENS ? (
				<div className="notification-sentence">
					<span className="notification-sentence__word">When</span>
					{cardPicker}
					<Select
						className="notification-sentence__slot"
						aria-label="What happens to it"
						mode="multiple"
						maxTagCount={2}
						placeholder="is created"
						value={draft.events}
						onChange={events => patch({ events })}
						options={EVENT_VERBS}
					/>
				</div>
			) : null}

			{draft.shape === SHAPES.CHANGES ? (
				// The card comes FIRST in every shape, however the prose reads,
				// because every slot after it is answered out of the card's own
				// vocabulary: an element picker over a card nobody has named yet
				// is a control with nothing in it. The read-back underneath says
				// the sentence the way a person would say it.
				<div className="notification-sentence">
					<span className="notification-sentence__word">On</span>
					{cardPicker}
					<span className="notification-sentence__word">, when</span>
					<Select
						className="notification-sentence__slot"
						aria-label="Element that changes"
						placeholder="an element"
						value={draft.changedElement || undefined}
						onChange={name => patch({ changedElement: name, becomes: undefined })}
						options={elementOptions}
						showSearch
						filterOption={optionSearchFilter}
						popupMatchSelectWidth={false}
					/>
					<span className="notification-sentence__word">becomes</span>
					{becomesOptions ? (
						<Select
							className="notification-sentence__slot"
							aria-label="What it becomes"
							placeholder="any value"
							allowClear
							value={draft.becomes === undefined ? undefined : draft.becomes}
							onChange={value => patch({ becomes: value === undefined ? undefined : value })}
							options={becomesOptions}
							showSearch
							filterOption={optionSearchFilter}
							popupMatchSelectWidth={false}
							disabled={!draft.changedElement}
						/>
					) : (
						<Input
							{...NO_TEXT_ASSIST}
							className="notification-sentence__slot"
							aria-label="What it becomes"
							placeholder="any value"
							value={draft.becomes === undefined ? '' : draft.becomes}
							onChange={event => patch({ becomes: event.target.value || undefined })}
							disabled={!draft.changedElement}
						/>
					)}
				</div>
			) : null}

			{draft.shape === SHAPES.APPROACHES ? (
				<div className="notification-sentence">
					<span className="notification-sentence__word">On</span>
					{cardPicker}
					<span className="notification-sentence__word">,</span>
					<Select
						className="notification-sentence__slot notification-sentence__slot--offsets"
						aria-label="Lead times in days"
						mode="tags"
						placeholder="60"
						tokenSeparators={[',', ' ']}
						open={false}
						suffixIcon={null}
						value={(draft.offsetsDays || []).map(String)}
						// Typed as text, stored as numbers, and sorted furthest-out
						// first — which is the order the watcher walks them and the
						// order "60, 30, 20, 10, 5" reads in.
						onChange={values => patch({
							offsetsDays: [...new Set(values
								.map(value => Number.parseInt(value, 10))
								.filter(days => Number.isFinite(days) && days >= 0))]
								.sort((a, b) => b - a)
								.slice(0, MAX_OFFSETS),
						})}
					/>
					<span className="notification-sentence__word">days</span>
					<Select
						className="notification-sentence__slot notification-sentence__slot--narrow"
						aria-label="Before or after"
						value={draft.direction}
						onChange={direction => patch({ direction })}
						options={DIRECTIONS}
					/>
					<Select
						className="notification-sentence__slot"
						aria-label="Dates to watch"
						mode="multiple"
						maxTagCount={2}
						placeholder="a date"
						value={dateValues}
						onChange={pickDates}
						options={dateOptions}
						showSearch
						filterOption={optionSearchFilter}
						popupMatchSelectWidth={false}
					/>
					<span className="notification-sentence__word">, at</span>
					{/* The platform's own time control, bare (no label wrapper): a
					    clock slot in a sentence still parses "9am" and still
					    stores 'HH:mm' on a 24-hour clock. */}
					<span className="notification-sentence__slot notification-sentence__slot--narrow">
						<DateField
							mode="time"
							elementName="notificationThresholdAt"
							elementLabel="Time of day it runs"
							value={draft.at}
							onChange={(name, next) => patch({ at: next || '09:00' })}
						/>
					</span>
					<Select
						className="notification-sentence__slot"
						aria-label="How many notices"
						value={draft.coalesce}
						onChange={coalesce => patch({ coalesce })}
						options={entryGroup ? ENTRY_COALESCE : COALESCE}
					/>
				</div>
			) : null}

			<ReadBack text={describeTriggerDraft(draft, {
				cardLabel: cardOptions.find(option => option.value === draft.cardId)?.card?.name,
				elementLabel,
				valueLabel: draft.shape === SHAPES.CHANGES ? becomesLabel : '',
				groupLabel: labelOf(watchedGroup),
				columnLabel,
			})} />

			{draft.shape === SHAPES.APPROACHES && entryGroup && watchedGroup ? (
				// Which ENTRIES are watched (KPR-836): the group's own columns,
				// the way a rule that watches entries narrows them (KPR-687) —
				// "a dose not yet given". Stored as one block on the group.
				<div className="notification-editor__condition" data-testid="notification-entry-condition">
					<div className="notification-editor__condition-row">
						<Text type="secondary">Only entries where</Text>
						{/* Keyed by the group: the builder reads its value once,
						    and a condition on another group's columns must not
						    linger on screen after the draft dropped it. */}
						<ConditionBuilder
							key={entryGroup}
							value={draft.entryConditionKql || ''}
							onChange={text => patch({ entryConditionKql: text || '' })}
							elements={watchedColumns}
							collectionId={collectionId}
							systemFields={false}
							emptyText="No conditions — every entry is watched."
						/>
					</div>
				</div>
			) : null}

			{draft.cardId ? (
				<div className="notification-editor__condition">
					<div className="notification-editor__condition-row">
						<Text type="secondary">Only if</Text>
						{/* The shared builder over this card's own vocabulary. `me`
						    is refused above the personal tier for the same reason
						    a dynamic group's rule refuses it: the rule is
						    evaluated for everyone at once and has nobody to be. */}
						<FilterBuilder
							collectionId={collectionId}
							scopeKey={`notification:${collectionId || 'global'}`}
							fields={fields}
							query={draft.conditionKql || ''}
							onApply={text => patch({ conditionKql: text || '' })}
							emptyText="No conditions — every one of these fires it."
							placeholder="priority >= 2"
							disallowMe={!allowMe}
						/>
					</div>
					{draft.conditionKql ? (
						<FilterChips
							query={draft.conditionKql}
							fields={fields}
							readOnly
							className="notification-editor__chips"
						/>
					) : null}
				</div>
			) : null}
		</section>
	);
}
